import Stepper from 'react-stepper-horizontal';
import { Container, Form, ProgressBar } from 'react-bootstrap';
import { PageHeader } from '../components/Layout/PageHeader';
import { DataCollectionNotice } from '../components/Layout/DataCollectionNotice';
import { ApplicantInfo } from '../components/ApplicantInfo';
import { AboveGroundInstallation } from '../components/AboveGroundFuelTankPermit/AboveGroundInstallation';
import { AboveGroundTankDetails } from '../components/AboveGroundFuelTankPermit/AboveGroundTankDetails';
import { PlotPlan } from '../components/PlotPlan';
import { SubmitSuccess } from '../components/SubmitSuccess';
import { InternalServerError } from '../components/Errors/InternalServerError';
import { SomethingBroke } from '../components/Errors/SomethingBroke';
import FormClientError from '../components/Errors/FormClientError';
import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';

export function FuelTankPermit() {
	const [step, setStep] = useState(1);

	const [httpStatusCode, setHttpStatusCode] = useState(null);

	const [somethingBroke, setSomethingBroke] = useState(false);

	const [submitDone, setSubmitDone] = useState(false);

	const [currApplicant, setCurrApplicant] = useState({
		name: '',
		phone: '',
		address: '',
		city: '',
		state: 'UT',
		zip: '',
		email: '',
	});

	const [currInstallationLocation, setCurrInstallationLocation] = useState({
		address: '',
		city: '',
		state: 'UT',
		zip: '',
	});

	const [
		currAboveGroundInstallationDetails,
		setCurrAboveGroundInstallationDetails,
	] = useState({
		numberOfTanks: '',
		tankSize: '',
		contents: 'gas',
		tankType: '',
		installer: '',
		timePeriod: '',
		removalDate: '',
		contentDescription: '',
	});

	const [currPlotPlan, setCurrPlotPlan] = useState({
		file: null,
		answer: null,
	});

	const createBody = plotPlan => {
		const body = new FormData();
		const data = {
			name: currApplicant.name,
			phone: currApplicant.phone,
			city: currApplicant.city,
			email: currApplicant.email,
			state: currApplicant.state,
			mailingAddress: currApplicant.address,
			zip: currApplicant.zip,
			installationAddress: currInstallationLocation.address,
			installationCity: currInstallationLocation.city,
			installationZip: currInstallationLocation.zip,
			installationState: currInstallationLocation.state,
			numberOfTanks: currAboveGroundInstallationDetails.numberOfTanks,
			tankSize: currAboveGroundInstallationDetails.tankSize,
			installer: currAboveGroundInstallationDetails.installer,
			tankType: currAboveGroundInstallationDetails.tankType,
			installationType: currAboveGroundInstallationDetails.timePeriod,
			removalDate: currAboveGroundInstallationDetails.removalDate,
			plotAttached: plotPlan.answer,
			date: dayjs().format('MM/DD/YYYY'),
			contents: currAboveGroundInstallationDetails.contents,
			contentDescription: currAboveGroundInstallationDetails.contentDescription,
		};

		body.append('file', plotPlan.file);
		for (var key in data) {
			body.append(key, data[key]);
		}
		return body;
	};

	const submitForm = async plotPlan => {
		try {
			const { status } = await fetch(
				`${process.env.REACT_APP_API_PREFIX_PROTO}${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_API_PREFIX}/fuel-tank-permit`,
				{
					method: 'POST',
					body: createBody(plotPlan),
				}
			);

			setHttpStatusCode(status);
			setSubmitDone(false);
		} catch (err) {
			console.error(err);
			setSomethingBroke(true);
		}
	};

	const incrementStep = useCallback(() => {
		setStep(curStep => curStep + 1);
	}, []);

	const decrementStep = useCallback(() => {
		setStep(curStep => curStep - 1);
	}, []);

	const handleOnNext = (setData = () => {}) => {
		return data => {
			setData(data);
			incrementStep();
		};
	};

	const handleSubmit = (setData = () => {}) => {
		return async data => {
			setData(data);
			setSubmitDone(true);
			submitForm(data);
		};
	};

	useEffect(() => {
		if (httpStatusCode === 200) {
			incrementStep();
		}
	}, [httpStatusCode, incrementStep]);

	if (somethingBroke === true) {
		return <SomethingBroke></SomethingBroke>;
	}

	if (httpStatusCode >= 500) {
		return <InternalServerError></InternalServerError>;
	}

	if (httpStatusCode >= 400) {
		return (
			<FormClientError
				returnToForm={() => {
					setStep(1);
					setHttpStatusCode(null);
				}}
			/>
		);
	}

	const steps = [
		{
			component: (
				<ApplicantInfo
					currApplicant={currApplicant}
					onPrevious={decrementStep}
					onNext={handleOnNext(setCurrApplicant)}
					finalStep={false}
					disablePrev={true}
					disableNext={false}
					key="appInfo"
				/>
			),
			title: 'Applicant Information',
		},
		{
			component: (
				<AboveGroundInstallation
					currInstallationLocation={currInstallationLocation}
					onNext={handleOnNext(setCurrInstallationLocation)}
					finalStep={false}
					disableNext={false}
					onPrevious={decrementStep}
					disablePrev={false}
					key="aboveGndInst"></AboveGroundInstallation>
			),
			title: 'Installation Address',
		},
		{
			component: (
				<AboveGroundTankDetails
					key="aboveGndDetails"
					currAboveGroundInstallationDetails={
						currAboveGroundInstallationDetails
					}
					onNext={handleOnNext(setCurrAboveGroundInstallationDetails)}
					onPrevious={decrementStep}
					finalStep={false}
					disableNext={false}
					disablePrev={false}></AboveGroundTankDetails>
			),
			title: 'Installation Details',
		},
		{
			component: (
				<PlotPlan
					key="plotPlan"
					currPlotPlan={currPlotPlan}
					onPrevious={decrementStep}
					onSubmit={handleSubmit(setCurrPlotPlan)}
					finalStep={true}
					disablePrev={false}
					enableLoading={submitDone}
				/>
			),
			title: 'Plot Plan',
		},
		{
			component: (
				<SubmitSuccess
					finalStep={true}
					disablePrev={true}
					disableNext={true}
					key="submitSuccess"
				/>
			),
			title: 'Submission',
		},
	];

	return (
		<Container fluid className="p-0 mb-5 permit">
			<PageHeader title="Above Ground Fuel Tank Permit" />
			<Form className="app-container ">
				<DataCollectionNotice />

				<div className="stepper">
					<Stepper
						steps={steps.map(({ title }, i) => ({
							title,
							onClick: () => setStep(i + 1),
						}))}
						activeStep={step - 1}
					/>
				</div>

				<div className="bar">
					<ProgressBar className="mb-4" now={(step / steps.length) * 100} />
				</div>

				{steps.map(({ component }, index) =>
					step === index + 1 ? component : ''
				)}
			</Form>
		</Container>
	);
}
